import styled from '@emotion/styled';
import isUndefined from 'lodash/isUndefined';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { SOURCE_TYPES } from 'src/modules/user/models/user';
import { CrisisModal } from 'src/shared/components/CrisisModal';
import { Button } from '@modernhealth/vital-components';
import { mediaQueries } from 'src/shared/styles/variables';

import {
    FieldType,
    MappedAnswers,
    MultiSelectQuestion as IMultiSelectQuestion,
    Question,
    SingleSelectQuestion as ISingleSelectQuestion,
} from '../models/assessments';
import { AssessmentQuestion } from './AssessmentQuestion';

const Wrapper = styled.div({
    display: 'flex',
    width: '100%',
    height: '100%',
    flexGrow: 1,
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 100,
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const QuestionWrapper = styled.div({
    position: 'relative',
    textAlign: 'center',
    [mediaQueries.sm]: {
        width: '100%',
        paddingBottom: 0,
    },
});

const AnimationContainer = styled.div<{ direction: 'left' | 'right'; isMultiSelect: boolean }>(
    ({ direction, isMultiSelect }) => ({
        maxWidth: isMultiSelect ? 1100 : 700,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',

        '&.assessment-enter': {
            opacity: 0,
            transform: `translate(${direction === 'left' ? '50%' : '-50%'}, 0)`,
        },
        '&.assessment-enter-active': {
            opacity: 1,
            transition: 'transform 250ms ease-in, opacity 350ms ease-in',
            transform: 'translate(0,0)',
        },
        '&.assessment-exit': {
            transform: 'translate(0,0)',
        },
        '&.assessment-exit-active': {
            opacity: 0,
            transform: `translate(${direction === 'left' ? '-25%' : '25%'}, 0)`,
            transition: 'opacity 200ms ease-out, transform 250ms',
        },
    }),
);

const CrisisLink = styled.div({
    display: 'flex',
    justifyContent: 'center',
});

export const areAllRequiredQuestionsAnswered = (answers: MappedAnswers, questions: Question[]) => {
    const requiredQuestions = questions.filter(question => !question.optional);

    const answersValidity = requiredQuestions.map(question => {
        const answer = answers[question.questionKey];

        return answer !== '' && !isUndefined(answer);
    });

    return answersValidity.every(valid => valid === true);
};

interface Props {
    questions: Question[] | IMultiSelectQuestion[];
    onSubmit?: (answers: MappedAnswers) => void;
    onBack?: () => void;
    customHeader?: (question: IMultiSelectQuestion | ISingleSelectQuestion) => React.ReactNode;
    assessmentName: string;
    assessmentFlowUuid?: string;
    headerText?: string | null;
}

const valueForSkippedQuestion = null;

export const Assessment: React.FC<Props> = ({
    questions,
    onSubmit,
    customHeader,
    assessmentName,
    assessmentFlowUuid = 'No assessment flow uuid provided',
    onBack,
    headerText,
}) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({} as MappedAnswers);
    const [submitInProgress, setSubmitInProgress] = useState(false);
    const [crisisModalVisible, setCrisisModalVisible] = useState(false);
    const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('left');

    const isMounted = useRef(false);

    const currentQuestion = questions[step];

    const goToNextQuestion = () => {
        setAnimationDirection('left');
        setTimeout(() => {
            setStep(step + 1);
        }, 100);
    };

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    if (!questions.length) {
        return null;
    }

    const handleGoBack = () => {
        if (firstStep && onBack) {
            onBack();
            return;
        }

        setAnimationDirection('right');
        setTimeout(() => {
            setStep(step - 1);
        }, 100);
    };

    const handleSubmit = async (optionalAnswers?: MappedAnswers) => {
        if (onSubmit) {
            setSubmitInProgress(true);

            await onSubmit(optionalAnswers ? optionalAnswers : answers);

            if (!isMounted.current) {
                return;
            }

            setSubmitInProgress(false);
        }
    };

    const answer = answers[questions[step].questionKey];
    const isQuestionSkipped = questions[step].optional && answer === valueForSkippedQuestion;
    const isCurrentQuestionAnswered =
        isQuestionSkipped || (answer !== undefined && answer?.toString() !== '');

    const questionsAmount = questions.length;
    const isSurveyValid = areAllRequiredQuestionsAnswered(answers, questions);
    const firstStep = step === 0;
    const lastStep = step === questionsAmount - 1;
    const shouldDisplayBackButton = !firstStep || !!onBack;

    const handleQuestionAnswerChange = (
        questionId: string,
        value: number | null | string[] | string,
    ) => {
        const newAnswers = {
            ...answers,
            [questionId]: value,
        };

        setAnswers(newAnswers);
        return newAnswers;
    };

    const shouldPreventSubmission = lastStep && !isSurveyValid;
    const isContinueButtonDisabled = shouldPreventSubmission || !isCurrentQuestionAnswered;

    const handleQuestionSkip = (questionId: string) => {
        const value = currentQuestion.type === FieldType.MultiSelect ? [] : valueForSkippedQuestion;
        const answers = handleQuestionAnswerChange(questionId, value);
        if (lastStep) {
            handleSubmit(answers);
        } else {
            goToNextQuestion();
        }
    };

    const handleGoToNextQuestion = () => {
        if (lastStep) {
            handleSubmit();
        } else {
            goToNextQuestion();
        }
    };

    return (
        <Wrapper>
            <QuestionWrapper data-testid={`QuestionContainer_${step}`}>
                <SwitchTransition>
                    <CSSTransition key={step} timeout={400} classNames="assessment">
                        <AnimationContainer
                            direction={animationDirection}
                            isMultiSelect={currentQuestion.type === FieldType.MultiSelect}
                        >
                            <AssessmentQuestion
                                assessmentName={assessmentName}
                                customHeader={customHeader}
                                assessmentFlowUuid={assessmentFlowUuid}
                                question={currentQuestion as IMultiSelectQuestion}
                                headerText={headerText}
                                answer={answers[currentQuestion.questionKey]}
                                onAnswerChange={handleQuestionAnswerChange}
                                onAnswerSubmit={handleGoToNextQuestion}
                                shouldDisplayBackButton={shouldDisplayBackButton}
                                handleGoBack={handleGoBack}
                                submitInProgress={submitInProgress}
                                isContinueButtonDisabled={isContinueButtonDisabled}
                                lastStep={lastStep}
                                handleQuestionSkip={handleQuestionSkip}
                            />
                            {currentQuestion.displayCrisisWarning && (
                                <CrisisLink>
                                    <Button
                                        onClick={() => setCrisisModalVisible(true)}
                                        variant="flat"
                                        size="large"
                                    >
                                        {t('phq9.need_crisis_support')}
                                    </Button>
                                </CrisisLink>
                            )}

                            <CrisisModal
                                visible={crisisModalVisible}
                                source={SOURCE_TYPES.PHQ_9_LINK}
                                onClose={() => {
                                    setCrisisModalVisible(false);
                                }}
                            />
                        </AnimationContainer>
                    </CSSTransition>
                </SwitchTransition>
            </QuestionWrapper>
        </Wrapper>
    );
};
